import { z } from 'zod';
import { quantitySchema } from '../../validation';

/**
 * `Features` — resolved capability flags for an org.
 *
 * Computed by `FeaturesService.getForOrg({ orgId })`:
 *   FREE_DEFAULTS -> plan.features -> addon.features -> ClientConfig overrides.
 *
 * NOT a stored collection — this is the derived shape returned by the
 * features API and merged in the client-config payload. Sources are:
 *   - subscription plan (from SUBSCRIPTION_CONFIG.plans)
 *   - subscription addons (from SUBSCRIPTION_CONFIG.addons)
 *   - subscription.addonOverrides (per-org admin overrides)
 *   - client-config admin overrides (final say)
 *
 * IMPORTANT — naming distinction:
 *   - `FeaturesService` (this module)  -> "did the org pay for this capability?"
 *   - `useFeature(flagKey)` (PostHog)   -> "is this code path enabled for this user / cohort (A/B)?"
 *
 * Both coexist. A page can require both: PostHog flag ON + subscription feature unlocked.
 */
export const featuresSchema = z.object({
  /** Maximum vehicles allowed on this org. */
  maxVehicles: quantitySchema.optional(),
  /** Soft cap (overage billed) vs hard cap (refuse new vehicle creation). */
  maxVehiclesPolicy: z.enum(['soft', 'hard']).optional(),

  /** Maximum orders allowed per billing period. */
  maxOrders: quantitySchema.optional(),
  /** Soft cap (overage billed) vs hard cap (refuse new order creation). */
  maxOrdersPolicy: z.enum(['soft', 'hard']).optional(),

  /** Modules unlocked by the current plan/addons. */
  modules: z
    .object({
      order: z.boolean().optional(),
      fleet: z.boolean().optional(),
      crm: z.boolean().optional(),
      api: z.boolean().optional(),
    })
    .default({}),

  /** Third-party integrations unlocked. */
  integrations: z.array(z.string()).default([]),

  /**
   * Set true when the platform billing status blocks the org
   * (cancelled, past_due past grace, paused). UI uses this to render
   * the "billing-blocked" splash.
   */
  isBlocked: z.boolean().default(false),
});
export type Features = z.infer<typeof featuresSchema>;

/**
 * Partial feature patch — used by addon configs to merge their unlock
 * deltas into the resolved Features. Lets an addon declare
 * `features: { integrations: ['whatsapp'] }` without listing the
 * inherited modules.
 */
export const featuresPatchSchema = featuresSchema.deepPartial();
export type FeaturesPatch = z.infer<typeof featuresPatchSchema>;
